<template>
    <div class="w-full">
        <!-- Área de upload -->
        <div class="relative">
            <!-- Preview da imagem -->
            <div v-if="imagePreview" class="relative mb-4">
                <img :src="imagePreview" alt="Preview" class="w-full max-w-full h-auto rounded-lg border-2 border-gray-200 shadow-sm"/>
                <button 
                    @click="removeImage"
                    class="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg"
                >
                    ×
                </button>
            </div>

            <!-- Área de upload quando não há imagem -->
            <div v-else>
                <!-- Drag & Drop Area -->
                <div 
                    @drop="handleDrop"
                    @dragover.prevent
                    @dragenter.prevent
                    class="studio-upload-area border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-all duration-300 cursor-pointer relative overflow-hidden min-h-[350px] flex items-center justify-center"
                    :class="{ 'border-blue-400 bg-blue-50': isDragging }"
                    @click="triggerFileInput"
                >
                    <!-- Conteúdo sobre a imagem de fundo -->
                    <div class="relative z-10">
                        <svg class="w-12 h-12 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                        </svg>
                        <p class="text-gray-700 mb-2 font-medium">Clique para selecionar ou arraste uma imagem</p>
                        <p class="text-sm text-gray-500">PNG, JPG, GIF até 10MB</p>
                    </div>
                </div>

                <!-- Input file oculto -->
                <input 
                    ref="fileInput"
                    type="file"
                    accept="image/*"
                    @change="handleFileSelect"
                    class="hidden"
                />

                <!-- Separador OU -->
                <div class="flex items-center my-4">
                    <div class="flex-1 border-t border-gray-300"></div>
                    <span class="px-4 text-gray-500 text-sm">OU</span>
                    <div class="flex-1 border-t border-gray-300"></div>
                </div>

                <!-- Input para URL -->
                <div class="flex gap-2">
                    <input 
                        v-model="imageUrl"
                        type="url"
                        placeholder="Cole a URL da imagem aqui..."
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        @keyup.enter="loadImageFromUrl"
                    />
                    <button 
                        @click="loadImageFromUrl"
                        :disabled="!imageUrl"
                        class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                        Carregar
                    </button>
                </div>
            </div>
        </div>

        <!-- Mensagem de erro -->
        <div v-if="error" class="mt-2 text-red-600 text-sm">
            {{ error }}
        </div>
    </div>
</template>

<script>
export default {
    name: "ImageUploader",
    
    props: {
        modelValue: {
            type: String,
            default: ''
        },
        maxSize: {
            type: Number,
            default: 10 * 1024 * 1024 // 10MB
        }
    },

    emits: ['update:modelValue', 'imageSelected'],

    data() {
        return {
            imagePreview: this.modelValue,
            imageUrl: '',
            isDragging: false,
            error: '',
            selectedFile: null
        }
    },

    watch: {
        modelValue(newValue) {
            this.imagePreview = newValue;
        }
    },

    methods: {
        triggerFileInput() {
            this.$refs.fileInput.click();
        },

        handleFileSelect(event) {
            const file = event.target.files[0];
            if (file) {
                this.processFile(file);
            }
        },

        handleDrop(event) {
            event.preventDefault();
            this.isDragging = false;
            
            const files = event.dataTransfer.files;
            if (files.length > 0) {
                this.processFile(files[0]);
            }
        },

        processFile(file) {
            this.error = '';

            // Validar tipo de arquivo
            if (!file.type.startsWith('image/')) {
                this.error = 'Por favor, selecione apenas arquivos de imagem.';
                return;
            }

            // Validar tamanho
            if (file.size > this.maxSize) {
                this.error = `Arquivo muito grande. Máximo permitido: ${this.formatFileSize(this.maxSize)}`;
                return;
            }

            // Criar preview
            const reader = new FileReader();
            reader.onload = (e) => {
                this.imagePreview = e.target.result;
                this.selectedFile = file;
                this.$emit('update:modelValue', this.imagePreview);
                this.$emit('imageSelected', {
                    file: file,
                    preview: this.imagePreview,
                    type: 'file'
                });
            };
            reader.readAsDataURL(file);
        },

        async loadImageFromUrl() {
            if (!this.imageUrl) return;

            this.error = '';
            
            try {
                // Validar se é uma URL válida
                new URL(this.imageUrl);
                
                // Verificar se a imagem carrega
                const img = new Image();
                img.onload = () => {
                    this.imagePreview = this.imageUrl;
                    this.selectedFile = null;
                    this.$emit('update:modelValue', this.imagePreview);
                    this.$emit('imageSelected', {
                        url: this.imageUrl,
                        preview: this.imagePreview,
                        type: 'url'
                    });
                    this.imageUrl = '';
                };
                img.onerror = () => {
                    this.error = 'Não foi possível carregar a imagem desta URL.';
                };
                img.src = this.imageUrl;
                
            } catch (e) {
                this.error = 'URL inválida. Verifique o endereço e tente novamente.';
            }
        },

        removeImage() {
            this.imagePreview = '';
            this.selectedFile = null;
            this.imageUrl = '';
            this.error = '';
            this.$emit('update:modelValue', '');
            this.$emit('imageSelected', null);
            
            // Limpar input file
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        }
    }
}
</script>

<style scoped>
.studio-upload-area {
    background-image: url('/imgs/studio.png');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    position: relative;
}

.studio-upload-area::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url('/imgs/studio.png');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    filter: grayscale(100%) opacity(0.15);
    transition: all 0.3s ease;
    z-index: 1;
}

.studio-upload-area:hover::before {
    filter: grayscale(0%) opacity(0.25);
}

.studio-upload-area::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.85);
    z-index: 2;
}
</style> 